import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { 
  Search, 
  TrendingUp, 
  MapPin, 
  Home, 
  DollarSign, 
  Calendar,
  ExternalLink,
  Star,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Eye,
  Calculator
} from 'lucide-react';
import { masterSupabase as supabase } from '../../../lib/supabase-master';


const ObjectFinder = ({ onAnalyzeProperty }) => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchProperties = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('property_suggestions')
        .select('*')
        .eq('is_active', true)
        .order('investment_score', { ascending: false })
        .limit(5);

      if (error) throw error;
      setProperties(data || []);
    } catch (err) {
      setError(err.message);
      console.error('Fehler beim Laden der Objekte:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProperties();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchProperties();
  };

  const handleAnalyze = (property) => {
    if (onAnalyzeProperty) {
      onAnalyzeProperty({
        purchasePrice: property.price,
        livingSpace: property.living_space,
        rooms: property.rooms,
        yearBuilt: property.year_built,
        monthlyRent: property.monthly_rent,
        address: property.address,
        propertyType: property.property_type
      });
    }
  };

  const getScoreColor = (score) => {
    if (score >= 85) return 'text-green-600 bg-green-50';
    if (score >= 75) return 'text-blue-600 bg-blue-50';
    if (score >= 65) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'KAUFEN': return 'bg-green-100 text-green-800';
      case 'PRÜFEN': return 'bg-blue-100 text-blue-800';
      case 'BEOBACHTEN': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Objektfinder lädt...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              KI-Objektfinder
            </CardTitle>
            <Button 
              onClick={handleRefresh} 
              disabled={refreshing}
              variant="outline"
              size="sm"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Aktualisieren
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{properties.length}</div>
              <div className="text-sm text-gray-600">Aktuelle Objekte</div>
            </div>
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {properties.filter(p => p.investment_score >= 80).length}
              </div>
              <div className="text-sm text-gray-600">Top-Empfehlungen</div>
            </div>
            <div className="text-center p-4 bg-yellow-50 rounded-lg">
              <div className="text-2xl font-bold text-yellow-600">
                {properties.length > 0 ? 
                  `${(properties.reduce((sum, p) => sum + p.gross_yield, 0) / properties.length).toFixed(1)}%` : 
                  'N/A'
                }
              </div>
              <div className="text-sm text-gray-600">Ø Rendite</div>
            </div>
          </div>
          
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Automatisch gefilterte Investment-Objekte aus Rhein-Neckar-Region. 
              Nur Objekte mit Investment-Score &gt;70 werden angezeigt.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Fehler beim Laden der Objekte: {error}
          </AlertDescription>
        </Alert>
      )}

      {/* Properties List */}
      {properties.length === 0 && !error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Search className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Keine Objekte gefunden
            </h3>
            <p className="text-gray-500">
              Aktuell sind keine neuen Investment-Objekte verfügbar.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {properties.map((property) => (
            <Card key={property.id} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Property Info */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {property.title}
                        </h3>
                        <div className="flex items-center gap-2 text-gray-600 mb-2">
                          <MapPin className="h-4 w-4" />
                          <span className="text-sm">{property.address}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getScoreColor(property.investment_score)} border-0`}>
                          <Star className="h-3 w-3 mr-1" />
                          {property.investment_score}
                        </Badge>
                        <Badge className={getActionColor(property.ai_recommendation?.action || 'PRÜFEN')}>
                          {property.ai_recommendation?.action || 'PRÜFEN'}
                        </Badge>
                      </div>
                    </div>

                    {/* Key Metrics */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="h-4 w-4 text-green-600" />
                        <div>
                          <div className="text-sm text-gray-500">Preis</div>
                          <div className="font-semibold">{property.price.toLocaleString()}€</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4 text-blue-600" />
                        <div>
                          <div className="text-sm text-gray-500">Fläche</div>
                          <div className="font-semibold">{property.living_space}m²</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <TrendingUp className="h-4 w-4 text-purple-600" />
                        <div>
                          <div className="text-sm text-gray-500">Rendite</div>
                          <div className="font-semibold">{property.gross_yield}%</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-orange-600" />
                        <div>
                          <div className="text-sm text-gray-500">Baujahr</div>
                          <div className="font-semibold">{property.year_built || 'N/A'}</div>
                        </div>
                      </div>
                    </div>

                    {/* AI Recommendation */}
                    {property.ai_recommendation && (
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <h4 className="font-medium text-gray-900 mb-2">KI-Bewertung:</h4>
                        {property.ai_recommendation.reasons?.length > 0 && (
                          <div className="mb-2">
                            <div className="text-sm font-medium text-green-700 mb-1">Vorteile:</div>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {property.ai_recommendation.reasons.map((reason, index) => (
                                <li key={index}>{reason}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {property.ai_recommendation.risks?.length > 0 && (
                          <div className="mb-2">
                            <div className="text-sm font-medium text-red-700 mb-1">Risiken:</div>
                            <ul className="text-sm text-gray-600 list-disc list-inside">
                              {property.ai_recommendation.risks.map((risk, index) => (
                                <li key={index}>{risk}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {property.description}
                    </p>

                    {/* Source Info */}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Quelle: {property.source_platform}</span>
                      <span>
                        Gefunden: {new Date(property.created_at).toLocaleDateString('de-DE')}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 lg:w-48">
                    <Button 
                      onClick={() => handleAnalyze(property)}
                      className="w-full"
                    >
                      <Calculator className="h-4 w-4 mr-2" />
                      Analysieren
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => window.open(property.source_url, '_blank')}
                      className="w-full"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Exposé ansehen
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="w-full"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ObjectFinder;
